import { globals } from "@/globals";
import * as signals from "@/signals";
import { getRandomInteger, showDialog, updateSettings } from "@/utils";
import { t } from "i18next";

class LocalWallpaper {
	private static readonly DATABASE_NAME = "wallpaper";

	public static instance: LocalWallpaper | null = null;
	private database: IDBDatabase | null;
	public isVideo: boolean;
	public videoThumbnailUrl: string | null;
	public wallpaperFile: File | null;
	public wallpaperUrl: string | null;

	public constructor() {
		LocalWallpaper.instance = this;
		this.database = null;
		this.isVideo = false;
		this.videoThumbnailUrl = null;
		this.wallpaperFile = null;
		this.wallpaperUrl = null;
	}

	private async connectDatabase(): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			if (this.database) {
				resolve();
				return;
			}
			const dbRequest = indexedDB.open(globals.APP_NAME, 1);
			dbRequest.onsuccess = (): void => {
				this.database = dbRequest.result;
				resolve();
			};
			dbRequest.onerror = reject;
			dbRequest.onupgradeneeded = (): void => {
				this.database = null;
				const store = dbRequest.result.createObjectStore("wallpaper");
				store.transaction.oncomplete = (): void => {
					this.database = store.transaction.db;
					resolve();
				};
			};
		});
	}

	public async createVideoThumbnail(): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			if (!this.isVideo) {
				resolve();
				return;
			}
			if (!this.wallpaperUrl) {
				reject(new Error("Wallpaper URL is not set"));
				return;
			}
			const video = document.createElement("video");
			video.setAttribute("src", this.wallpaperUrl);
			video.setAttribute("width", "270");
			video.setAttribute("height", "180");
			video.currentTime = 1;
			video.addEventListener("loadeddata", () => {
				const canvas = document.createElement("canvas");
				canvas.width = video.width;
				canvas.height = video.height;
				canvas
					.getContext("2d")
					?.drawImage(video, 0, 0, video.width, video.height);
				canvas.toBlob((blob) => {
					if (blob) {
						this.videoThumbnailUrl = URL.createObjectURL(blob);
					}
					resolve();
				});
			});
		});
	}

	public async deleteWallpaper(): Promise<void> {
		this.resetFields();
		await this.connectDatabase();
		if (!this.database) {
			return;
		}
		this.database
			.transaction(LocalWallpaper.DATABASE_NAME, "readwrite")
			.objectStore(LocalWallpaper.DATABASE_NAME)
			.clear();
	}

	public static getInstance(): LocalWallpaper {
		if (!LocalWallpaper.instance) {
			return new LocalWallpaper();
		}
		return LocalWallpaper.instance;
	}

	public async getWallpaper(): Promise<File | null> {
		try {
			if (this.wallpaperFile) {
				return this.wallpaperFile;
			}
			await this.connectDatabase();
			if (!this.database) {
				return null;
			}
			const objectStore = this.database
				.transaction(LocalWallpaper.DATABASE_NAME)
				.objectStore(LocalWallpaper.DATABASE_NAME);
			const keys = await this.promisfyRequest<string[]>(
				objectStore.getAllKeys(),
			);
			if (keys.length === 0) {
				return null;
			}
			const randomKey = keys[getRandomInteger(0, keys.length - 1)];
			const file = await this.promisfyRequest<File>(
				objectStore.get(randomKey),
			);
			this.loadFields(file);
			return file;
		} catch (error) {
			console.error(error);
			return null;
		}
	}

	public async getWallpaperUrl(): Promise<string | null> {
		if (this.wallpaperUrl) {
			return this.wallpaperUrl;
		}
		const wallpaper = await this.getWallpaper();
		if (!wallpaper) {
			return null;
		}
		this.wallpaperUrl = URL.createObjectURL(wallpaper);
		return this.wallpaperUrl;
	}

	private loadFields(file: File): void {
		this.isVideo = file.type.startsWith("video/");
		this.wallpaperFile = file;
		this.wallpaperUrl = URL.createObjectURL(file);
		this.videoThumbnailUrl = null;
	}

	public async loadWallpaper(): Promise<void> {
		const wallpaper = await this.getWallpaper();
		if (!wallpaper) {
			return;
		}
		this.setWallpaper();
	}

	private promisfyRequest<T>(request: IDBRequest): Promise<T> {
		return new Promise<T>((resolve, reject) => {
			request.onerror = (): void => {
				reject(new Error(request.error?.message));
			};
			request.onsuccess = (): void => {
				resolve(request.result as T);
			};
		});
	}

	private resetFields(): void {
		this.isVideo = false;
		this.wallpaperFile = null;
		if (this.videoThumbnailUrl) {
			URL.revokeObjectURL(this.videoThumbnailUrl);
			this.videoThumbnailUrl = null;
		}
		if (this.wallpaperUrl) {
			URL.revokeObjectURL(this.wallpaperUrl);
			this.wallpaperUrl = null;
		}
	}

	public async saveWallpaper(files: FileList): Promise<void> {
		try {
			this.resetFields();
			await this.connectDatabase();
			if (!this.database) {
				return;
			}
			const objectStore = this.database
				.transaction(LocalWallpaper.DATABASE_NAME, "readwrite")
				.objectStore(LocalWallpaper.DATABASE_NAME);
			await this.promisfyRequest<void>(objectStore.clear());
			for (const file of files) {
				await this.promisfyRequest<void>(
					objectStore.put(file, file.name),
				);
			}
			await updateSettings({
				currentWallpaper: "Custom",
			});
			const randomFile = files[getRandomInteger(0, files.length - 1)];
			this.loadFields(randomFile);
			this.setWallpaper();
		} catch (error) {
			console.error(error);
			void showDialog(t("unableSetWallpaperInIncognito"));
		}
	}

	public setWallpaper(): void {
		signals.wallpaper.value = {
			isVideo: this.isVideo,
			url: this.wallpaperUrl || undefined,
		};
	}
}

export default LocalWallpaper;
